// Mini map preview for one simplification variant: renders the network inside
// the drawn selection rectangle as a small SVG — the current state in light
// gray underneath, the simplified variant in blue on top — so the user can
// compare the three degrees before picking one.
export const SimplifyPreview = ({
  before,
  after,
  selection,
  width = 170,
  height = 120,
}) => {
  const lats = selection.map((p) => p[0]);
  const lngs = selection.map((p) => p[1]);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  let minLng = Math.min(...lngs);
  let maxLng = Math.max(...lngs);
  // a bit of padding so nodes on the border are not cut in half
  const padLat = (maxLat - minLat) * 0.06 || 1e-5;
  const padLng = (maxLng - minLng) * 0.06 || 1e-5;
  minLat -= padLat;
  maxLat += padLat;
  minLng -= padLng;
  maxLng += padLng;

  // equirectangular projection in the selection area, uniform scale, centered
  const midLat = (minLat + maxLat) / 2;
  const lngStretch = Math.cos((midLat * Math.PI) / 180);
  const worldW = (maxLng - minLng) * lngStretch || 1e-9;
  const worldH = maxLat - minLat || 1e-9;
  const margin = 6;
  const scale = Math.min(
    (width - 2 * margin) / worldW,
    (height - 2 * margin) / worldH
  );
  const offsetX = (width - worldW * scale) / 2;
  const offsetY = (height - worldH * scale) / 2;

  const project = ([lat, lng]) => [
    offsetX + (lng - minLng) * lngStretch * scale,
    offsetY + (maxLat - lat) * scale,
  ];

  const inBounds = ([lat, lng]) =>
    lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;

  function renderNetwork(elements, color, nodeRadius, strokeWidth, keyPrefix) {
    const nodePositions = {};
    elements.forEach((el) => {
      if (el.type === "node") nodePositions[el.id] = el.position;
    });

    const shapes = [];
    elements.forEach((el) => {
      if (el.type !== "edge") return;
      const start = nodePositions[el.startNodeId];
      const end = nodePositions[el.endNodeId];
      if (!start || !end) return;
      // only edges that touch the preview window (SVG clips the overflow)
      if (!inBounds(start) && !inBounds(end)) return;
      const [x1, y1] = project(start);
      const [x2, y2] = project(end);
      shapes.push(
        <line
          key={`${keyPrefix}-${el.id}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      );
    });
    elements.forEach((el) => {
      if (el.type !== "node" || !inBounds(el.position)) return;
      const [cx, cy] = project(el.position);
      shapes.push(
        <circle
          key={`${keyPrefix}-${el.id}`}
          cx={cx}
          cy={cy}
          r={nodeRadius}
          fill={color}
        />
      );
    });
    return shapes;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        backgroundColor: "rgba(128,128,128,0.08)",
        borderRadius: "8px",
        display: "block",
      }}
    >
      {renderNetwork(before, "#b0b0b0", 2.5, 1.2, "before")}
      {renderNetwork(after, "#1976d2", 3.5, 2.2, "after")}
    </svg>
  );
};

export default SimplifyPreview;
